// Restore a saved session via /auth/me without logging the user out on
// transient failures. Only a real 401 means the token is bad; network errors,
// timeouts and 5xx (e.g. a backend restarting during a deploy) keep the token
// and retry with exponential backoff.

export const RESTORE_BACKOFF_MS = [1000, 2000, 4000, 8000, 15000, 15000];

export function isUnauthorized(error) {
  return error?.response?.status === 401;
}

/**
 * @param fetchMe   () => Promise<{ data }>
 * @returns Promise<{ status: 'ok', user } | { status: 'unauthorized' } | { status: 'unreachable', error } | { status: 'cancelled' }>
 */
export async function restoreSession(fetchMe, {
  backoffMs = RESTORE_BACKOFF_MS,
  sleep = (ms) => new Promise(r => setTimeout(r, ms)),
  isCancelled = () => false,
  onRetry = () => {},
} = {}) {
  let lastError = null;
  for (let attempt = 0; attempt <= backoffMs.length; attempt++) {
    if (isCancelled()) return { status: 'cancelled' };
    try {
      const res = await fetchMe();
      return { status: 'ok', user: res.data };
    } catch (error) {
      if (isUnauthorized(error)) return { status: 'unauthorized' };
      lastError = error;
      if (attempt === backoffMs.length) break;
      onRetry(attempt + 1, error);
      await sleep(backoffMs[attempt]);
    }
  }
  return { status: 'unreachable', error: lastError };
}
